import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TableModule, TableLazyLoadEvent, Table } from 'primeng/table';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { EntityDropdownPickerComponent } from '../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.component';
import { EntityDropdownPickerAdapter } from '../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { BusinessService } from '../business.service';
import { Business } from '../business.model';
import { Page } from '../../../models/page-wrapper.model';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import * as XLSX from 'xlsx';
import { BusinessUnitsService } from '../../business-units/business-units.service';
import { BusinessUnit } from '../../business-units/business-unit.model';
@Component({
  selector: 'app-business-list',
  templateUrl: './business-list.component.html',
  styleUrl: './business-list.component.css',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TableModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    RouterModule,
    ReactiveFormsModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    IftaLabelModule,
    EntityDropdownPickerComponent,
  ],
  providers: [ConfirmationService, MessageService],
})
export class BusinessListComponent implements OnInit {
    @ViewChild('dt') dt!: Table;
  business: Business[] = [];
  totalRecords = 0;
  isLoading = false;
  filterForm!: FormGroup;
  filterSub!: Subscription;
  businessUnitPickerAdapter!: EntityDropdownPickerAdapter<BusinessUnit>;
  constructor(
     private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private businessService: BusinessService,
    private businessUnitsService: BusinessUnitsService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

   ngOnInit() {
    this.buildFilterForm();
    this.businessUnitPickerAdapter = this.businessUnitsService.createBusinessUnitPickerAdapter();
    this.filterSub = this.filterForm.valueChanges
      .pipe(
        debounceTime(500), // Wait for 500ms of inactivity
        distinctUntilChanged() // Only emit if the value has changed
      )
      .subscribe(() => {
        // Reset paginator to the first page and trigger a reload
        this.dt.reset();
      });
  }
  buildFilterForm() {
    this.filterForm = this.fb.group({
      businessName: [''],
      businessCode: [''],
      businessUnit: [null],
    });
  }

  onSearch(): void {
    this.dt.reset();
  }

  onReset(): void {
    this.filterForm.reset({
      businessName: '',
      businessCode: '',
      businessUnit: null,
    });
    this.dt.reset();
  }

  loadBusinesss(
    event?: TableLazyLoadEvent,
    isExport: boolean = false,
    callback?: (business: Business[]) => void
  ) {
    this.isLoading = true;
    let params: any;

    if (isExport) {
      params = {
        page: 0,
        size: 2000,
        businessName: this.filterForm.value.businessName,
        businessCode: this.filterForm.value.businessCode,
        businessUnitId: this.filterForm.value.businessUnit?.id,
      };
    } else {
      const size = event?.rows ?? 10;
      const page = event?.first ? Math.floor(event.first / size) : 0;
      params = {
        page,
        size,
        businessName: this.filterForm.value.businessName,
        businessCode: this.filterForm.value.businessCode,
        businessUnitId: this.filterForm.value.businessUnit?.id,
      };
    }
    
     Object.keys(params).forEach((key) => {
      if (!params[key]) {
        delete params[key];
      }
    });

    this.businessService.getAllBusinesss(params).subscribe({
      next: (page: Page<Business>) => {
        this.business = page.content;
        this.totalRecords = page.totalElements;
        this.isLoading = false;
        if (callback) {
          callback(page.content);
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load Businesss units.',
        });
      },
    });
  }

  deleteBusiness(id: number): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this Business?',
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',

      acceptLabel: 'Yes, Delete',
      rejectLabel: 'Cancel',

      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-success',

      accept: () => {
        this.businessService.deleteBusiness(id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `Business Deleted successfully.`,
            });
            setTimeout(() => {
              this.loadBusinesss();
            }, 500);
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: `Failed to Delete Business.`,
            });
            console.error(err);
          },
        });
      },
    });
  }

  createBusiness() {
    this.router.navigate(
      ['.', { outlets: { canvasOutlet: ['business', 'create'] } }],
      { relativeTo: this.route.parent?.parent }
    );
  }

  editBusiness(id: number): void {
    this.router.navigate(
      ['.', { outlets: { canvasOutlet: ['business', 'edit', id] } }],
      { relativeTo: this.route.parent?.parent }
    );
  }

  exportExcel(): void {
    this.loadBusinesss(undefined, true, (business: Business[]) => {
      const dataToExport = business.map((business) => ({
        'Business Name': business.businessName,
        'Business Code': business.businessCode,
        'Business Unit': business.businessUnitObject?.businessUnitName ?? business.businessUnitId ?? '-',
        'AR Account': business.arAccount ?? '-',
        'AP Account': business.apAccount ?? '-',
        Reference: business.reference ?? '-',
      }));

      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'business');

      XLSX.writeFile(wb, 'Business_Export.xlsx');
    });
  }
}
